import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import { typographyStyles } from '../styles/typography';
import { createT } from '../utils/simpleTranslate';
import { formatBirthdateForPDF, formatDateForPDF } from '../utils/dataFormatters';
import { getPatientID } from '../../../utils/index.jsx';

/**
 * List Patients PDF Document Component
 * 
 * Renders a list of patients scheduled for a specific date.
 * Used for daily appointment lists in the outpatient department.
 */

interface IPatientData {
  name?: string;
  surnames?: string;
  birthdate?: string | Date;
  phone?: string;
  healthId?: string; // Calculated health ID from getPatientID function
  [key: string]: any; // Allow other fields from API (including raw ID fields for getPatientID)
}

interface IPatient {
  uuid: string;
  id?: string | number;
  personalData: IPatientData;
}

interface IResearcher {
  uuid?: string;
  name?: string;
  surnames?: string;
}

interface IAgenda {
  id: string;
  uuid?: string;
  name?: string;
  principalResearcher?: IResearcher;
}

interface IAppointment {
  uuid: string;
  patient: {
    uuid: string;
  };
  agendaId?: string;
}

interface ListPatientsPDFDocumentProps {
  appointments: IAppointment[];
  patients: IPatient[];
  selectedDate?: Date;
  config: PDFConfig;
  agendas?: IAgenda[];
}

// Helper function to get patient full name
const getPatientFullName = (personalData?: IPatientData): string => {
  if (!personalData) return '';
  const name = personalData.name || '';
  const surnames = personalData.surnames || '';
  return `${name} ${surnames}`.trim();
};

// Helper function to get researcher full name
const getResearcherFullName = (researcher?: IResearcher): string => {
  if (!researcher) return '';
  const name = researcher.name || '';
  const surnames = researcher.surnames || '';
  return `${name} ${surnames}`.trim();
};


export const ListPatientsPDFDocument: React.FC<ListPatientsPDFDocumentProps> = ({
  appointments,
  patients,
  selectedDate,
  config,
  agendas = [],
}) => {
  // Normalize locale to supported language codes
  const normalizedLocale = config.locale?.toLowerCase().startsWith('en') ? 'en' : 
                           config.locale?.toLowerCase().startsWith('es') ? 'es' : 'fr';
  
  const t = createT(normalizedLocale);

  // Build patient rows from appointments
  const patientRows = React.useMemo(() => {
    return appointments.map((appointment) => {
      const patient = patients.find(p => p.uuid === appointment.patient.uuid);
      if (!patient) return null;

      // Find the agenda/doctor for this appointment
      const agenda = agendas.find(a => a.id === appointment.agendaId);
      
      return {
        namePatient: getPatientFullName(patient.personalData),
        dateOfBirth: patient.personalData.birthdate 
          ? formatBirthdateForPDF(patient.personalData.birthdate, normalizedLocale)
          : '',
        phone: patient.personalData.phone || '',
        healthId: getPatientID(patient),
        doctor: agenda ? getResearcherFullName(agenda.principalResearcher) : '',
      };
    }).filter((row): row is NonNullable<typeof row> => row !== null);
  }, [appointments, patients, agendas, normalizedLocale]);
  
  // Build title with date and patient count
  const documentTitle = React.useMemo(() => {
    let title = t('pages.hospital.outpatients.patients_list', 'Liste des patients');
    if (selectedDate) {
      title += ` - ${formatDateForPDF(selectedDate, normalizedLocale)}`;
    }
    // Add patient count
    const patientWord = t('general.patient', 'patient');
    title += ` (${patientRows.length} ${patientWord}${patientRows.length > 1 ? 's' : ''})`;
    return title;
  }, [selectedDate, patientRows.length, normalizedLocale, t]);

  // Table styles
  const tableStyles = {
    table: {
      width: '100%',
      marginTop: 15,
    },
    headerRow: {
      flexDirection: 'row' as const,
      borderBottomWidth: 2,
      borderBottomColor: '#000',
      paddingBottom: 5,
      marginBottom: 5,
      backgroundColor: '#f5f5f5',
      paddingTop: 5,
      paddingHorizontal: 5,
    },
    headerCell: {
      ...typographyStyles.body2,
      fontWeight: 'bold',
      fontSize: 10,
    },
    row: {
      flexDirection: 'row' as const,
      paddingVertical: 5,
      paddingHorizontal: 5,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      minHeight: 25,
    },
    cell: {
      ...typographyStyles.body2,
      fontSize: 9,
    },
    // Column widths - adjusted for better fit
    nameCell: { width: '25%', paddingRight: 5 },
    birthCell: { width: '15%', paddingRight: 5 },
    phoneCell: { width: '20%', paddingRight: 5 },
    healthIdCell: { width: '18%', paddingRight: 5 },
    doctorCell: { width: '22%' },
  };

  const renderTable = () => {
    if (patientRows.length === 0) {
      return (
        <View style={{ marginTop: 30, alignItems: 'center' }}>
          <Text style={[typographyStyles.h6, { fontWeight: 'bold', color: '#666' }]}>
            {t('pages.hospital.outpatients.no_appointments', 'Aucun rendez-vous pour cette date')}
          </Text>
        </View>
      );
    }

    return (
      <View style={tableStyles.table}>
        {/* Table Header */}
        <View style={tableStyles.headerRow}>
          <Text style={[tableStyles.headerCell, tableStyles.nameCell]}>
            {t('general.patient', 'Patient')}
          </Text>
          <Text style={[tableStyles.headerCell, tableStyles.birthCell]}>
            {t('investigation.create.personal_data.fields.birthdate', 'Date de naissance')}
          </Text>
          <Text style={[tableStyles.headerCell, tableStyles.phoneCell]}>
            {t('investigation.create.personal_data.fields.phone', 'Téléphone')}
          </Text>
          <Text style={[tableStyles.headerCell, tableStyles.healthIdCell]}>
            {t('investigation.create.personal_data.fields.health_id', 'ID Santé')}
          </Text>
          <Text style={[tableStyles.headerCell, tableStyles.doctorCell]}>
            {t('hospital.doctor.label', 'Médecin')}
          </Text>
        </View>

        {/* Table Body */}
        {patientRows.map((row, index) => (
          <View key={index} style={tableStyles.row}>
            <Text style={[tableStyles.cell, tableStyles.nameCell]}>
              {row.namePatient}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.birthCell]}>
              {row.dateOfBirth}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.phoneCell]}>
              {row.phone}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.healthIdCell]}>
              {row.healthId}
            </Text>
            <Text style={[tableStyles.cell, tableStyles.doctorCell]}>
              {row.doctor}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  return (
    <PDFDocument
      title={documentTitle}
      config={config}
    >
      {renderTable()}
    </PDFDocument>
  );
};

export default ListPatientsPDFDocument;

